import React, { useEffect } from 'react';
import {
    Users,
    MapPin,
    Search,
    Building2,
    ChevronRight,
    Crown,
    Grid,
    List
} from 'lucide-react';

// Import des données - DÉCOMMENTEZ cette ligne quand vous aurez créé le fichier
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { COMMISSARIAT_POLITIQUE_DATA, getCommissariatStats } from '../constants/commissariatPolitiqueData';

// IMPORTANT: Remplacez cette section par l'import réel une fois le fichier créé
// import { COMMISSARIAT_POLITIQUE_DATA } from '../constants/commissariatPolitiqueData';

const CommissariatPolitiquePage: React.FC = () => {
    const [selectedProvince, setSelectedProvince] = React.useState<string | null>(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [viewMode, setViewMode] = React.useState<'grid' | 'list'>('grid');
    const [expandedDepartments, setExpandedDepartments] = React.useState<Set<string>>(new Set());
    const [visibleProvinces, setVisibleProvinces] = React.useState<Set<string>>(new Set());

    useEffect(() => {
        // Scroll vers le haut au chargement
        window.scrollTo(0, 0);

        // Animation progressive des provinces
        const timer = setTimeout(() => {
            COMMISSARIAT_POLITIQUE_DATA.forEach((province, index) => {
                setTimeout(() => {
                    setVisibleProvinces(prev => {
                        const newSet = new Set(prev);
                        newSet.add(province.id);
                        return newSet;
                    });
                }, index * 150);
            });
        }, 300);

        return () => clearTimeout(timer);
    }, []);

    // Calcul des statistiques
    const stats = React.useMemo(() => {
        const totalCommissaires = COMMISSARIAT_POLITIQUE_DATA.reduce((sum, p) => sum + p.totalCommissaires, 0);
        const totalWomen = COMMISSARIAT_POLITIQUE_DATA.reduce((sum, p) => sum + p.totalWomen, 0);
        const totalMen = totalCommissaires - totalWomen;

        return {
            totalCommissaires,
            totalWomen,
            totalMen,
            womenPercentage: Math.round((totalWomen / totalCommissaires) * 100),
            menPercentage: Math.round((totalMen / totalCommissaires) * 100),
            totalProvinces: COMMISSARIAT_POLITIQUE_DATA.length,
            totalDepartements: COMMISSARIAT_POLITIQUE_DATA.reduce((sum, p) => sum + p.departements.length, 0)
        };
    }, []);

    // Filtrage des données
    const filteredProvinces = React.useMemo(() => {
        if (!searchTerm.trim() && !selectedProvince) return COMMISSARIAT_POLITIQUE_DATA;

        return COMMISSARIAT_POLITIQUE_DATA.filter(province => {
            const matchesSearch = !searchTerm.trim() ||
                province.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                province.departements.some(dept => dept.name.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesProvince = !selectedProvince || province.id === selectedProvince;

            return matchesSearch && matchesProvince;
        });
    }, [searchTerm, selectedProvince]);

    const toggleDepartment = (deptId: string) => {
        setExpandedDepartments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(deptId)) {
                newSet.delete(deptId);
            } else {
                newSet.add(deptId);
            }
            return newSet;
        });
    };

    const getProvinceColor = (color: string) => {
        const colorMap: { [key: string]: { bg: string; text: string; border: string; hover: string } } = {
            'primary-blue': {
                bg: 'bg-primary-blue',
                text: 'text-primary-blue',
                border: 'border-primary-blue',
                hover: 'hover:bg-primary-blue'
            },
            'primary-green': {
                bg: 'bg-primary-green',
                text: 'text-primary-green',
                border: 'border-primary-green',
                hover: 'hover:bg-primary-green'
            },
            'primary-yellow': {
                bg: 'bg-primary-yellow',
                text: 'text-primary-yellow',
                border: 'border-primary-yellow',
                hover: 'hover:bg-primary-yellow'
            }
        };
        return colorMap[color] || colorMap['primary-blue'];
    };

    const renderDepartementContent = (departement: any, province: any) => {
        const colors = getProvinceColor(province.color);
        const deptId = `${province.id}-${departement.code}`;
        const isExpanded = expandedDepartments.has(deptId);

        // Comptage des commissaires
        let totalCommissaires = 0;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        let totalWomen = 0;

        // Compter commissaires départementaux
        departement.commissairesDepartementaux?.forEach((c: any) => {
            totalCommissaires++;
            if (c.isWoman) totalWomen++;
        });

        // Compter commissaires communaux
        departement.commissairesCommunaux?.forEach((c: any) => {
            totalCommissaires++;
            if (c.isWoman) totalWomen++;
        });

        // Compter dans les communes/arrondissements
        departement.communes?.forEach((commune: any) => {
            commune.arrondissements?.forEach((arr: any) => {
                arr.commissaires?.forEach((c: any) => {
                    totalCommissaires++;
                    if (c.isWoman) totalWomen++;
                });
            });
            commune.cantons?.forEach((canton: any) => {
                canton.commissaires?.forEach((c: any) => {
                    totalCommissaires++;
                    if (c.isWoman) totalWomen++;
                });
            });
        });

        return (
            <div
                key={deptId}
                className="bg-white border border-gray-300 hover:border-gray-400 transition-all duration-300"
            >
                {/* En-tête du département avec design flat */}
                <div
                    className={`p-6 cursor-pointer border-l-4 ${colors.border} hover:bg-gray-50 transition-colors`}
                    onClick={() => toggleDepartment(deptId)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className={`w-12 h-12 ${colors.bg} border border-gray-400 flex items-center justify-center text-white`}>
                                <Building2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 text-lg">
                                    {departement.name}
                                </h4>
                                {departement.ville && (
                                    <p className="text-gray-500 flex items-center mt-1">
                                        <MapPin className="w-4 h-4 mr-1" />
                                        {departement.ville}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center space-x-6">
                            <div className="text-right">
                                <div className={`${colors.text} font-bold text-2xl`}>
                                    {totalCommissaires}
                                </div>

                            </div>
                            <ChevronRight
                                className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${
                                    isExpanded ? 'rotate-90' : ''
                                }`}
                            />
                        </div>
                    </div>
                </div>

                {/* Contenu détaillé avec design flat */}
                {isExpanded && (
                    <div className="px-6 pb-6 border-t border-gray-300">
                        {/* Commissaires départementaux */}
                        {departement.commissairesDepartementaux?.length > 0 && (
                            <div className="mt-6">
                                <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                                    <Crown className="w-5 h-5 mr-2 text-yellow-600" />
                                    Commissaires Départementaux ({departement.commissairesDepartementaux.length})
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {departement.commissairesDepartementaux.map((commissaire: any, idx: number) => (
                                        <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 border border-gray-200">
                                            <div className={`w-3 h-3 ${commissaire.isWoman ? 'bg-pink-400' : 'bg-blue-400'} border border-gray-300`} />
                                            <span className="text-gray-800 font-medium">{commissaire.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Commissaires communaux directs */}
                        {departement.commissairesCommunaux?.length > 0 && (
                            <div className="mt-6">
                                <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                                    <Users className="w-5 h-5 mr-2 text-blue-600" />
                                    Commissaires Communaux ({departement.commissairesCommunaux.length})
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {departement.commissairesCommunaux.map((commissaire: any, idx: number) => (
                                        <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 border border-gray-200">
                                            <div className={`w-3 h-3 ${commissaire.isWoman ? 'bg-pink-400' : 'bg-blue-400'} border border-gray-300`} />
                                            <span className="text-gray-800 font-medium">{commissaire.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Communes avec arrondissements - Design flat */}
                        {departement.communes?.map((commune: any, communeIdx: number) => (
                            <div key={communeIdx} className="mt-6">
                                <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                                    <MapPin className="w-5 h-5 mr-2 text-green-600" />
                                    {commune.name}
                                </h5>

                                {commune.arrondissements?.map((arr: any, arrIdx: number) => (
                                    <div key={arrIdx} className="ml-6 mb-4">
                                        <h6 className="font-medium text-gray-700 mb-2">
                                            {arr.name} ({arr.commissaires?.length || 0} commissaires)
                                        </h6>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {arr.commissaires?.map((commissaire: any, cIdx: number) => (
                                                <div key={cIdx} className="flex items-center space-x-2 p-2 bg-gray-50 border border-gray-200">
                                                    <div className={`w-2 h-2 ${commissaire.isWoman ? 'bg-pink-400' : 'bg-blue-400'} border border-gray-300`} />
                                                    <span className="text-gray-700">{commissaire.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {commune.cantons?.map((canton: any, cantonIdx: number) => (
                                    <div key={cantonIdx} className="ml-6 mb-4">
                                        <h6 className="font-medium text-gray-700 mb-2">
                                            {canton.name} ({canton.commissaires?.length || 0} commissaires)
                                        </h6>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {canton.commissaires?.map((commissaire: any, cIdx: number) => (
                                                <div key={cIdx} className="flex items-center space-x-2 p-2 bg-gray-50 border border-gray-200">
                                                    <div className={`w-2 h-2 ${commissaire.isWoman ? 'bg-pink-400' : 'bg-blue-400'} border border-gray-300`} />
                                                    <span className="text-gray-700">{commissaire.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-16">

            {/* Header avec breadcrumb */}


            {/* Hero section avec image de fond et statistiques intégrées */}
            <div className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
                {/* Image de fond avec voile vert */}
                <div className="absolute inset-0 z-0">
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: `url('/images/orgbg.webp')`,
                        }}
                    />
                    {/* Voile vert */}
                    <div className="absolute inset-0 bg-primary-green/75"></div>
                    {/* Overlay noir pour plus de contraste */}
                    <div className="absolute inset-0 bg-black/20"></div>
                </div>

                {/* Contenu du hero */}
                <div className="relative z-10 container mx-auto px-4 text-center text-white">
                    <br/><br/><br/>
                    <div
                        className="inline-flex items-center space-x-2 text-primary-yellow text-sm mb-6 bg-white/10 backdrop-blur-sm px-4 py-2 border border-white/20">
                        <Building2 className="w-4 h-4"/>
                        <span className="font-medium">Structure Organisationnelle</span>
                    </div>

                    <h1 className="text-5xl md:text-6xl font-light text-white mb-6">
                        Commissariat Politique de l'UDB
                    </h1>

                    <div className="w-20 h-1 bg-primary-yellow mx-auto mb-8"></div>

                    <p className="text-xl text-white/90 font-light max-w-3xl mx-auto mb-12">
                        Découvrez la structure organisationnelle complète de l'Union Démocratique des Bâtisseurs
                        à travers les 9 provinces du Gabon
                    </p>

                    {/* Statistiques intégrées dans le hero - 2 cartes avec espacement */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
                        <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20">
                            <div className="text-4xl font-bold text-white mb-2">{stats.totalCommissaires}</div>
                            <div className="text-white/80 font-medium">Commissaires</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20">
                            <div className="text-4xl font-bold text-white mb-2">{stats.totalProvinces}</div>
                            <div className="text-white/80 font-medium">Provinces</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section principale */}
            <section className="py-16 relative overflow-hidden">
                {/* Éléments décoratifs de fond */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-32 h-32 bg-primary-blue/5 rounded-full blur-3xl"></div>
                    <div className="absolute top-40 right-20 w-48 h-48 bg-primary-green/5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 left-1/3 w-40 h-40 bg-primary-yellow/5 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-4 relative z-10 pt-8">
                    <div className="max-w-7xl mx-auto">
                        {/* Barre de recherche et filtres - Design flat moderne */}
                        <div className="mb-12 space-y-6">
                            {/* Navigation rapide par provinces */}
                            <div className="bg-white/95 backdrop-blur-sm p-6 border border-gray-300">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                    <MapPin className="w-5 h-5 mr-2 text-primary-blue" />
                                    Navigation rapide par provinces
                                </h3>
                                <div className="grid grid-cols-3 md:grid-cols-9 gap-3">
                                    {COMMISSARIAT_POLITIQUE_DATA.map((province, index) => {
                                        const isSelected = selectedProvince === province.id;
                                        // Alterner entre vert et bleu seulement
                                        const useGreen = index % 2 === 0;
                                        const colorClass = useGreen ? 'bg-primary-green' : 'bg-primary-blue';
                                        const textClass = useGreen ? 'text-primary-green' : 'text-primary-blue';
                                        const borderClass = useGreen ? 'border-primary-green' : 'border-primary-blue';
                                        const hoverClass = useGreen ? 'hover:bg-primary-green' : 'hover:bg-primary-blue';

                                        return (
                                            <button
                                                key={province.id}
                                                onClick={() => setSelectedProvince(isSelected ? null : province.id)}
                                                className={`p-3 border-2 transition-all ${
                                                    isSelected
                                                        ? `${colorClass} text-white border-transparent`
                                                        : `bg-white ${textClass} ${borderClass} ${hoverClass} hover:text-white`
                                                }`}
                                            >
                                                <div className="text-xs font-bold mb-1">{province.code}</div>
                                                <div className="text-xs">{province.name}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Filtres et recherche */}
                            <div className="flex flex-col lg:flex-row gap-6 items-center">
                                {/* Recherche */}
                                <div className="relative flex-1 max-w-lg">
                                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher une province, département, commissaire..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-12 pr-4 py-4 border border-gray-400 focus:outline-none focus:border-primary-blue bg-white/95 backdrop-blur-sm text-base"
                                    />
                                </div>

                                {/* Actions */}
                                <div className="flex items-center space-x-4">
                                    {/* Reset filters */}
                                    {(selectedProvince || searchTerm) && (
                                        <button
                                            onClick={() => {
                                                setSelectedProvince(null);
                                                setSearchTerm('');
                                            }}
                                            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-400 hover:bg-gray-50 transition-colors"
                                        >
                                            Réinitialiser
                                        </button>
                                    )}

                                    {/* Mode d'affichage */}
                                    <div className="flex border border-gray-400">
                                        <button
                                            onClick={() => setViewMode('grid')}
                                            className={`px-4 py-3 transition-colors ${
                                                viewMode === 'grid' ? 'bg-primary-blue text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Grid className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => setViewMode('list')}
                                            className={`px-4 py-3 border-l border-gray-300 transition-colors ${
                                                viewMode === 'list' ? 'bg-primary-blue text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            <List className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contenu principal */}
                        {filteredProvinces.length > 0 ? (
                            <div className="grid gap-8">
                                {filteredProvinces.map((province, provinceIndex) => {
                                    const colors = getProvinceColor(province.color);
                                    const isVisible = visibleProvinces.has(province.id);

                                    return (
                                        <div
                                            key={province.id}
                                            className={`transition-all duration-700 ${
                                                isVisible
                                                    ? 'opacity-100 translate-y-0'
                                                    : 'opacity-0 translate-y-8'
                                            }`}
                                            style={{
                                                transitionDelay: `${provinceIndex * 150}ms`
                                            }}
                                        >
                                            {/* En-tête de la province avec design flat moderne */}
                                            <div className={`${colors.bg} text-white p-8`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-6">
                                                        <div className="w-20 h-20 bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
                                                            <img
                                                                src={`/images/provinces/${province.id}.jpg`}
                                                                alt={`Province ${province.name}`}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    // Fallback vers les initiales si l'image ne charge pas
                                                                    e.currentTarget.style.display = 'none';
                                                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                                                    if (fallback) fallback.style.display = 'flex';
                                                                }}
                                                            />
                                                            {/* Fallback avec initiales */}
                                                            <span className="text-2xl font-bold hidden">{province.code}</span>
                                                        </div>
                                                        <div>
                                                            <h2 className="text-3xl font-bold mb-2">
                                                                {province.name}
                                                            </h2>
                                                            <p className="text-white/90 text-lg">
                                                                {province.totalCommissaires} commissaires
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-4xl font-bold">{province.totalCommissaires}</div>

                                                    </div>
                                                </div>
                                            </div>

                                            {/* Liste des départements avec design flat */}
                                            <div className="bg-white border border-gray-300 border-t-0 p-6">
                                                <div className="space-y-4">
                                                    {province.departements.map((departement) =>
                                                        renderDepartementContent(departement, province)
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Search className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun résultat trouvé</h3>
                                <p className="text-gray-500">
                                    Essayez d'ajuster vos critères de recherche
                                </p>
                            </div>
                        )}

                        {/* Call to Action avec design flat moderne - Adhésion au parti */}
                        <div className="mt-16 bg-gradient-to-r from-primary-blue to-primary-green text-white p-8 border border-gray-400 text-center">
                            <h3 className="text-2xl font-bold mb-4">Rejoignez l'Union Démocratique des Bâtisseurs</h3>
                            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
                                Participez activement à la construction du Gabon de demain en adhérant à notre mouvement politique.
                                Ensemble, bâtissons le Gabon nouveau !
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={() => window.location.href = '/adhesion'}
                                    className="bg-white text-primary-blue px-8 py-3 font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center space-x-2 border border-gray-400"
                                >
                                    <Users className="w-5 h-5" />
                                    <span>Adhérer au parti UDB</span>
                                </button>

                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CommissariatPolitiquePage;
